import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useMemo, useState } from "react";

import type { ManagedMcpServer } from "./useManagedMcpServers.ts";
import type { McpAccessLogEvent } from "./useMcpAccessLogs.ts";

type EventsResp = { events: McpAccessLogEvent[] };

const FETCH_LIMIT = 200;

const groupKey = (e: McpAccessLogEvent): string | undefined => {
  const sessionId = e.event?.sessionId;
  const messageId = e.event?.message?.id;
  if (!sessionId || messageId === undefined) return undefined;
  return `${sessionId}:${messageId}`;
};

const toolNamesFromResult = (result: unknown): string[] => {
  if (!result || typeof result !== "object") return [];
  const tools = (result as { tools?: unknown }).tools;
  if (!Array.isArray(tools)) return [];
  return tools
    .map((tool) =>
      tool && typeof tool === "object"
        ? (tool as { name?: unknown }).name
        : undefined
    )
    .filter((name): name is string => typeof name === "string" && !!name);
};

/**
 * Extracts advertised tool names from a server-scoped event window. Pairs
 * `tools/list` request + response by `${sessionId}:${messageId}` and reads
 * the result's `tools[].name` array. Pure — events must already be scoped
 * to a single server (server-side filter on `event.mcp-server.id`).
 */
const toolsFromEvents = (
  events: readonly McpAccessLogEvent[]
): ReadonlySet<string> => {
  const listRequests = new Set<string>();
  for (const event of events) {
    if (event.event?.message?.kind !== "request") continue;
    if (event.event.message.method !== "tools/list") continue;
    const key = groupKey(event);
    if (key) listRequests.add(key);
  }
  const out = new Set<string>();
  for (const response of events) {
    if (response.event?.message?.kind !== "response") continue;
    const key = groupKey(response);
    if (!key || !listRequests.has(key)) continue;
    for (const name of toolNamesFromResult(response.event.message.result)) {
      out.add(name);
    }
  }
  return out;
};

/**
 * Resolves advertised tool names per managed MCP server by fanning out one
 * `?serverId=<id>` query per server and pairing `tools/list` request +
 * response in the response. Each query is index-bounded to that server's
 * traffic, so the total cost scales with `servers.length` rather than the
 * tenant-wide event firehose.
 */
export const useToolsForServers = (
  servers: readonly Pick<ManagedMcpServer, "id">[]
): {
  toolsByServer: Map<string, ReadonlySet<string>>;
  loading: boolean;
} => {
  const authFetch = useAuthFetch();
  const [toolsByServer, setToolsByServer] = useState<
    Map<string, ReadonlySet<string>>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const ids = useMemo(
    () =>
      servers
        .map((s) => s.id)
        .sort()
        .join(","),
    [servers]
  );

  useGuardedEffect(
    (cancellation) => async () => {
      if (!ids) {
        setToolsByServer(new Map());
        return;
      }
      setLoading(true);
      try {
        const entries = await Promise.all(
          ids.split(",").map(async (id) => {
            const params = new URLSearchParams({
              serverId: id,
              limit: String(FETCH_LIMIT),
            });
            const res = await authFetch(
              `mcp-access-log/events?${params.toString()}`,
              { method: "GET", cancellation }
            );
            if (!res || cancellation.isCancelled) {
              return [id, new Set<string>() as ReadonlySet<string>] as const;
            }
            const body = (await res.json()) as EventsResp;
            return [id, toolsFromEvents(body.events ?? [])] as const;
          })
        );
        if (cancellation.isCancelled) return;
        setToolsByServer(new Map(entries));
      } finally {
        if (!cancellation.isCancelled) setLoading(false);
      }
    },
    [authFetch, ids],
    useCallback(() => {
      setToolsByServer(new Map());
      setLoading(false);
    }, [])
  );

  return { toolsByServer, loading };
};
